// State of the menu-history modal behind MENU_HISTORY_PAGINATION (Phase 4C-5).
// The first page is the newest entries; "load older" asks for entries after the last one shown (the backend `before` + `beforeId` cursor).
// Responses are plain arrays, so a full page means "there may be more". Every request carries a token: closing, reopening or switching store
// bumps it, and a late answer for an older token is dropped instead of showing another store's history.
import { useCallback, useEffect, useRef, useState } from "react";

export const MENU_HISTORY_PAGE_SIZE = 50;

const CLOSED = { open: false, loading: false, loadingMore: false, list: [], hasMore: false, error: null, moreError: null };

// The backend orders by (createdAt, _id) descending, so the next page starts after the last entry received: both values are sent, because
// entries sharing a createdAt would otherwise be skipped at the boundary. Null when the last entry cannot serve as a cursor.
const cursorAfter = (list) => {
  const last = list[list.length - 1];
  return last?.createdAt && last?._id ? { before: last.createdAt, beforeId: String(last._id) } : null;
};

// fetchPage(cursor) -> Promise<entry[]> (newest first); cursor is null for the first page, else { before, beforeId }; it is re-read on every call, so it may close over the current store / menu / item.
export default function useMenuHistory(fetchPage, pageSize = MENU_HISTORY_PAGE_SIZE) {
  const [state, setState] = useState(CLOSED);
  const token = useRef(0);
  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;
  const stateRef = useRef(state);
  stateRef.current = state;

  const loadFirst = useCallback(async () => {
    const mine = ++token.current;
    setState({ ...CLOSED, open: true, loading: true });
    try {
      const page = await fetchRef.current(null);
      if (token.current !== mine) return;
      setState({ ...CLOSED, open: true, list: page, hasMore: page.length >= pageSize });
    } catch (error) {
      if (token.current === mine) setState({ ...CLOSED, open: true, error });
    }
  }, [pageSize]);

  const loadMore = useCallback(async () => {
    const current = stateRef.current;
    if (!current.open || current.loading || current.loadingMore || !current.hasMore) return;
    const cursor = cursorAfter(current.list);
    if (!cursor) return setState((s) => ({ ...s, hasMore: false }));
    const mine = ++token.current;
    setState((s) => ({ ...s, loadingMore: true, moreError: null }));
    try {
      const page = await fetchRef.current(cursor);
      if (token.current !== mine) return;
      setState((s) => {
        const seen = new Set(s.list.map((entry) => String(entry._id)));
        const fresh = page.filter((entry) => !seen.has(String(entry._id)));
        // A full page of repeats would otherwise be asked for again with the same cursor.
        return { ...s, loadingMore: false, list: [...s.list, ...fresh], hasMore: page.length >= pageSize && fresh.length > 0 };
      });
    } catch (error) {
      if (token.current === mine) setState((s) => ({ ...s, loadingMore: false, moreError: error }));
    }
  }, [pageSize]);

  const close = useCallback(() => {
    token.current += 1;
    setState(CLOSED);
  }, []);

  // Unmount: nothing may land afterwards.
  useEffect(() => () => void (token.current += 1), []);

  return { ...state, openHistory: loadFirst, retry: loadFirst, loadMore, close };
}
